import type {ContextRegionElement} from '@github-ui/context-region-element'
import type {ContextRegionControllerElement} from '@github-ui/context-region-element/context-region-controller-element'
import type {
  ContextRegionActions,
  CrumbOptions,
  LocalNavigationActions,
  NavigationTabCountUpdate,
} from '@github-ui/global-navigation-types'

import {getCurrentOwnerRepo} from './utils/current-owner-repo'

export function formatNavigationTabCount(count: number | undefined) {
  if (count === undefined || count <= 0) {
    return undefined
  }
  if (count > 5000) {
    return '5k+'
  }
  if (count >= 1000) {
    return `${Math.round(count / 100) / 10}k`
  }
  return count.toString()
}

export function getCurrentRepositoryNwo(): string | undefined {
  const repository = getCurrentOwnerRepo()
  return repository ? `${repository.owner}/${repository.repo}` : undefined
}

export function isCurrentRepository(repositoryNwo: string | undefined): boolean {
  if (repositoryNwo === undefined) return true
  return getCurrentRepositoryNwo()?.toLowerCase() === repositoryNwo.toLowerCase()
}

// store a reference to the callback function that will be executed once the context region is connected
let pendingOperation = null as ((contextRegion: ContextRegionActions) => void) | null
let waitingForContextRegion = false
let registeredContextRegion: ContextRegionActions | undefined = undefined

/**
 * Registers a React-based context region handler with the global navigation system. This will take priority over any custom element-based search.
 * Only one context region can be registered at a time. If a pending operation exists,
 * it will be executed immediately upon registration.
 *
 * @param element - The context region actions interface to register
 */
export function registerContextRegionHandler(element: ContextRegionActions): void {
  if (registeredContextRegion && registeredContextRegion !== element) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error(
        `A context region is already registered with the global navigation! Multiple context regions are not supported.`,
      )
    }
    return
  }

  registeredContextRegion = element

  // If there's a pending operation, execute it now that the context region is registered
  if (pendingOperation) {
    pendingOperation(element)
    pendingOperation = null
  }
}

/** Reset the registered context region */
export function clearRegisteredContextRegion(): void {
  registeredContextRegion = undefined
}

/** Clear any pending operations waiting for the context region to connect */
export function clearPendingOperations(): void {
  pendingOperation = null
}

// The context-region element might not be available immediately on page load.
// This function sets up a one-time event listener that executes the pending operation
// when the context region connects to the DOM, removing the need for existence checks.
function waitForContextRegion(): void {
  // only set up the event listener once
  if (waitingForContextRegion) return

  waitingForContextRegion = true
  const contextRegionController = document.querySelector<ContextRegionControllerElement>('context-region-controller')

  if (!contextRegionController) {
    // If the context region's controller is not found, we can't set up the event listener
    return
  }

  contextRegionController.addEventListener(
    'context-region-connected',
    () => {
      if (pendingOperation) {
        pendingOperation(contextRegionController.contextRegion)
        pendingOperation = null
      }
    },
    {once: true},
  )
}

export function getContextRegion(options?: {
  source?: 'element' | 'react' | 'both'

  /** Optionally ignore errors when context region is not found */
  silent?: boolean
}): ContextRegionActions | null {
  const source = options?.source ?? 'both'
  const silent = options?.silent ?? process.env.NODE_ENV !== 'development'

  let contextRegion: ContextRegionActions | ContextRegionElement | null = null

  if (source === 'element' || source === 'both') {
    contextRegion = document.querySelector<ContextRegionElement>('context-region')
  }

  if (!contextRegion && (source === 'react' || source === 'both')) {
    contextRegion = registeredContextRegion ?? null
  }

  if (!contextRegion) {
    if (!silent) {
      // eslint-disable-next-line no-console
      console.error(
        `The global navigation's context region cannot be found! Make sure the <context-region> element exists on the page before calling any of the global navigation breadcrumb methods.`,
      )
    }

    return null
  }

  // Make sure the context region is fully connected to the DOM if it's an element
  if ('isConnected' in contextRegion && !contextRegion.isConnected) {
    return null
  }

  // Additional check to ensure custom methods are available (if one of the methods is missing, they likely all are)
  if (!contextRegion.pushCrumb || typeof contextRegion.pushCrumb !== 'function') {
    return null
  }

  return contextRegion
}

// Executes the callback immediately if context-region is ready, or queues it for when the element becomes available
export function withContextRegion(operation: (region: ContextRegionActions) => void): void {
  const contextRegion = getContextRegion()

  if (contextRegion) {
    operation(contextRegion)
    return
  }

  // if the context region is not ready, queue the operation and listen for readiness
  pendingOperation = operation
  waitForContextRegion()
}

export function pushNavigationBreadcrumb(crumb: CrumbOptions) {
  withContextRegion(contextRegion => contextRegion.pushCrumb(crumb))
}

export function popNavigationBreadcrumb() {
  withContextRegion(contextRegion => contextRegion.popCrumb())
}

export function replaceNavigationBreadcrumbs(crumbs: CrumbOptions[]) {
  withContextRegion(contextRegion => contextRegion.replaceCrumbs(crumbs))
}

export function renameCurrentNavigationBreadcrumb(label: string) {
  withContextRegion(contextRegion => contextRegion.renameCurrentCrumb(label))
}

export function replaceCurrentNavigationBreadcrumb(crumb: CrumbOptions) {
  withContextRegion(contextRegion => contextRegion.replaceCurrentCrumb(crumb))
}

let registeredLocalNavigation: LocalNavigationActions | undefined = undefined
export type NavigationTabCountUpdateResult = 'applied' | 'queued' | 'rejected'
export type NavigationTabCountResponseUpdateResult = NavigationTabCountUpdateResult | 'stale'
type NavigationTabCountUpdateOptions = {
  repositoryNwo?: string
  expectedUpdateVersion?: number
  expectedTargetVersion?: number
  signal?: AbortSignal
  onSettled?: (result: Exclude<NavigationTabCountUpdateResult, 'queued'>) => void
}
type PendingNavigationTabCountUpdate = {
  tabId: string
  update: Exclude<NavigationTabCountUpdate, {type: 'delta'}>
  repositoryNwo: string
  pagePath: string
  queuedVersion?: number
  onSettled?: NavigationTabCountUpdateOptions['onSettled']
}
const navigationTabCountUpdateVersions = new Map<string, number>()
const navigationTabCountAuthoritativeVersions = new Map<string, number>()
const navigationTabCountTargetVersions = new Map<string, number>()
const navigationTabCountTargets = new Map<
  string,
  {handler: LocalNavigationActions | undefined; legacyElement: HTMLElement | null}
>()
let pendingNavigationTabCount: PendingNavigationTabCountUpdate | undefined = undefined

function currentPagePath(): string {
  return `${window.location.pathname}${window.location.search}`
}

export function getNavigationTabCountUpdateVersion(tabId: string): number {
  return navigationTabCountUpdateVersions.get(tabId) ?? 0
}

export function getNavigationTabCountAuthoritativeVersion(tabId: string): number {
  return navigationTabCountAuthoritativeVersions.get(tabId) ?? 0
}

export function getNavigationTabCountTargetVersion(tabId: string): number {
  const target = {
    handler: registeredLocalNavigation,
    legacyElement: tabId === 'pull-requests' ? document.getElementById('pull-requests-repo-tab-count') : null,
  }
  const previousTarget = navigationTabCountTargets.get(tabId)
  if (
    previousTarget === undefined ||
    previousTarget.handler !== target.handler ||
    previousTarget.legacyElement !== target.legacyElement
  ) {
    navigationTabCountTargets.set(tabId, target)
    navigationTabCountTargetVersions.set(tabId, (navigationTabCountTargetVersions.get(tabId) ?? 0) + 1)
  }
  return navigationTabCountTargetVersions.get(tabId) ?? 0
}

export function getNavigationTabCountSnapshot(tabId: string): {count: number; exact: boolean} | undefined {
  const reactCount = registeredLocalNavigation?.getTabCount?.(tabId)
  if (reactCount) return reactCount
  if (tabId !== 'pull-requests') return undefined

  const pullRequestsTab = document.getElementById('pull-requests-repo-tab-count')
  if (!pullRequestsTab) return undefined
  const storedCount = pullRequestsTab.getAttribute('data-exact-count')
  const title = pullRequestsTab.title.trim()
  const count = storedCount === null ? Number(title.replace(/\D/g, '')) : Number(storedCount)
  if (!Number.isSafeInteger(count)) return undefined
  return {count, exact: storedCount !== null || !title.endsWith('+')}
}

function settlePendingNavigationTabCount(result: Exclude<NavigationTabCountUpdateResult, 'queued'>) {
  const pendingCount = pendingNavigationTabCount
  pendingNavigationTabCount = undefined
  pendingCount?.onSettled?.(result)
}

export function markNavigationTabCountUpdated(tabId: string, preservePending = false) {
  navigationTabCountUpdateVersions.set(tabId, getNavigationTabCountUpdateVersion(tabId) + 1)
  navigationTabCountAuthoritativeVersions.set(tabId, getNavigationTabCountAuthoritativeVersion(tabId) + 1)
  if (!preservePending && pendingNavigationTabCount?.tabId === tabId) settlePendingNavigationTabCount('rejected')
}

function acceptedNavigationTabCountUpdate<Result extends Exclude<NavigationTabCountUpdateResult, 'rejected'>>(
  tabId: string,
  result: Result,
  authoritative = false,
): Result {
  navigationTabCountUpdateVersions.set(tabId, getNavigationTabCountUpdateVersion(tabId) + 1)
  if (authoritative) {
    navigationTabCountAuthoritativeVersions.set(tabId, getNavigationTabCountAuthoritativeVersion(tabId) + 1)
  }
  if (result === 'applied' && pendingNavigationTabCount?.tabId === tabId) {
    settlePendingNavigationTabCount('rejected')
  }
  return result
}

/**
 * Registers a React-based local navigation handler. This allows external code
 * to update tab counts through the React tree rather than via direct DOM manipulation.
 */
export function registerLocalNavigationHandler(handler: LocalNavigationActions): () => void {
  registeredLocalNavigation = handler
  const pendingCount = pendingNavigationTabCount
  if (pendingCount) {
    navigationTabCountAuthoritativeVersions.set(
      'pull-requests',
      getNavigationTabCountAuthoritativeVersion('pull-requests') + 1,
    )
    if (!isCurrentRepository(pendingCount.repositoryNwo) || pendingCount.pagePath !== currentPagePath()) {
      settlePendingNavigationTabCount('rejected')
    } else if (
      pendingCount.queuedVersion !== undefined &&
      pendingCount.queuedVersion !== getNavigationTabCountUpdateVersion(pendingCount.tabId)
    ) {
      settlePendingNavigationTabCount('rejected')
    } else {
      const applied = handler.updateTabCount(pendingCount.tabId, pendingCount.update)
      if (applied === true) {
        settlePendingNavigationTabCount('applied')
      } else if (applied === false) {
        settlePendingNavigationTabCount('rejected')
      }
    }
  } else {
    markNavigationTabCountUpdated('pull-requests')
  }
  getNavigationTabCountTargetVersion('pull-requests')
  return () => clearRegisteredLocalNavigation(handler)
}

export function clearRegisteredLocalNavigation(handler?: LocalNavigationActions): void {
  if (handler === undefined) {
    registeredLocalNavigation = undefined
    settlePendingNavigationTabCount('rejected')
    navigationTabCountUpdateVersions.clear()
    navigationTabCountAuthoritativeVersions.clear()
    navigationTabCountTargetVersions.clear()
    navigationTabCountTargets.clear()
  } else if (registeredLocalNavigation === handler) {
    registeredLocalNavigation = undefined
  }
}

export function getLocalNavigation(): LocalNavigationActions | null {
  return registeredLocalNavigation ?? null
}

/**
 * Update a tab's count. Numeric updates are treated as deltas for compatibility.
 */
export function updateNavigationTabCount(tabId: string, update: number): boolean
export function updateNavigationTabCount(
  tabId: string,
  update: NavigationTabCountUpdate,
  options?: NavigationTabCountUpdateOptions,
): NavigationTabCountUpdateResult
export function updateNavigationTabCount(
  tabId: string,
  update: number | NavigationTabCountUpdate,
  options: NavigationTabCountUpdateOptions = {},
): boolean | NavigationTabCountUpdateResult {
  if (typeof update !== 'number') return updateStructuredNavigationTabCount(tabId, update, options)

  const localNav = getLocalNavigation()
  if (!localNav) return false

  const applied = localNav.updateTabCount(tabId, {type: 'delta', delta: update})
  if (applied === true) acceptedNavigationTabCountUpdate(tabId, 'applied')
  return true
}

function setLegacyPullRequestTabCount(pullRequestsTab: HTMLElement, count: number, exact = false): boolean {
  const normalizedCount = Math.max(count, 0)
  pullRequestsTab.toggleAttribute('hidden', normalizedCount === 0)
  if (exact) {
    pullRequestsTab.setAttribute('data-exact-count', normalizedCount.toString())
  } else {
    pullRequestsTab.removeAttribute('data-exact-count')
  }
  pullRequestsTab.title = normalizedCount > 5000 ? '5,000+' : normalizedCount.toLocaleString('en-US')
  pullRequestsTab.textContent = formatNavigationTabCount(normalizedCount) ?? '0'
  return true
}

function updateStructuredNavigationTabCount(
  tabId: string,
  update: NavigationTabCountUpdate,
  {repositoryNwo, expectedUpdateVersion, expectedTargetVersion, signal, onSettled}: NavigationTabCountUpdateOptions,
  responseUpdate = false,
): NavigationTabCountUpdateResult {
  if (
    signal?.aborted ||
    !isCurrentRepository(repositoryNwo) ||
    (expectedTargetVersion !== undefined && expectedTargetVersion !== getNavigationTabCountTargetVersion(tabId))
  ) {
    onSettled?.('rejected')
    return 'rejected'
  }
  let resolvedUpdate = update
  if (
    expectedUpdateVersion !== undefined &&
    expectedUpdateVersion !== getNavigationTabCountAuthoritativeVersion(tabId)
  ) {
    if (update.type !== 'delta' || update.expectedCountAfterAuthoritativeUpdate === undefined) {
      onSettled?.('rejected')
      return 'rejected'
    }
    resolvedUpdate = {
      type: 'countIfCurrent',
      count: update.expectedCountAfterAuthoritativeUpdate + update.delta,
      expectedCounts: [update.expectedCountAfterAuthoritativeUpdate],
    }
  }

  const localNav = getLocalNavigation()
  if (localNav) {
    const applied = localNav.updateTabCount(tabId, resolvedUpdate)
    if (applied !== undefined) {
      const result = applied
        ? acceptedNavigationTabCountUpdate(tabId, 'applied', resolvedUpdate.type === 'count')
        : 'rejected'
      onSettled?.(result)
      return result
    }
  }

  if (tabId !== 'pull-requests') {
    onSettled?.('rejected')
    return 'rejected'
  }

  const pullRequestsTab = document.getElementById('pull-requests-repo-tab-count')
  if (pullRequestsTab === null) {
    if (repositoryNwo !== undefined && resolvedUpdate.type !== 'delta') {
      settlePendingNavigationTabCount('rejected')
      const result = acceptedNavigationTabCountUpdate(tabId, 'queued', responseUpdate)
      const pendingCount: PendingNavigationTabCountUpdate = {
        tabId,
        update: resolvedUpdate,
        repositoryNwo,
        pagePath: currentPagePath(),
        queuedVersion: responseUpdate ? getNavigationTabCountUpdateVersion(tabId) : undefined,
        onSettled,
      }
      pendingNavigationTabCount = pendingCount
      if (signal) {
        const onAbort = () => {
          if (pendingNavigationTabCount === pendingCount) settlePendingNavigationTabCount('rejected')
        }
        pendingCount.onSettled = settlement => {
          signal.removeEventListener('abort', onAbort)
          onSettled?.(settlement)
        }
        signal.addEventListener('abort', onAbort, {once: true})
      }
      return result
    }
    onSettled?.('rejected')
    return 'rejected'
  }

  if (resolvedUpdate.type === 'count') {
    setLegacyPullRequestTabCount(pullRequestsTab, resolvedUpdate.count, resolvedUpdate.exact)
    const result = acceptedNavigationTabCountUpdate(tabId, 'applied', true)
    onSettled?.(result)
    return result
  }

  const storedCount = pullRequestsTab.getAttribute('data-exact-count')
  if (storedCount === null && pullRequestsTab.title.trim().endsWith('+')) {
    onSettled?.('rejected')
    return 'rejected'
  }

  const currentCount = storedCount === null ? Number(pullRequestsTab.title.replace(/\D/g, '')) : Number(storedCount)
  if (!Number.isSafeInteger(currentCount)) {
    onSettled?.('rejected')
    return 'rejected'
  }

  if (resolvedUpdate.type === 'delta') {
    setLegacyPullRequestTabCount(pullRequestsTab, currentCount + resolvedUpdate.delta, true)
    const result = acceptedNavigationTabCountUpdate(tabId, 'applied', responseUpdate)
    onSettled?.(result)
    return result
  }
  if (!resolvedUpdate.expectedCounts.includes(currentCount)) {
    onSettled?.('rejected')
    return 'rejected'
  }

  setLegacyPullRequestTabCount(pullRequestsTab, resolvedUpdate.count, true)
  const result = acceptedNavigationTabCountUpdate(tabId, 'applied', responseUpdate)
  onSettled?.(result)
  return result
}

export function updateNavigationTabCountFromResponse(
  tabId: string,
  update: Extract<NavigationTabCountUpdate, {type: 'count'}>,
  expectedUpdateVersion: number,
  options?: NavigationTabCountUpdateOptions,
): NavigationTabCountResponseUpdateResult {
  if (expectedUpdateVersion !== getNavigationTabCountUpdateVersion(tabId)) {
    options?.onSettled?.('rejected')
    return 'stale'
  }
  return updateStructuredNavigationTabCount(tabId, update, options ?? {}, true)
}

/**
 * Set the active local navigation tab by its selected-link identifier (matched against a tab's
 * `selectedLinks`). Uses the React-based handler if registered, returns false if no handler is available.
 */
export function setActiveNavigationTab(selectedLink: string): boolean {
  const localNav = getLocalNavigation()
  if (localNav) {
    localNav.setActiveTab(selectedLink)
    return true
  }
  return false
}
